import {
  TILE_WIDTH_ADJUSTMENT_DECREMENT,
  TILE_WIDTH_ADJUSTMENT_INCREMENT,
} from '@/components/diagrams/components/Tile';

import { BulkTileSizeChangeAction, EntityDiagramActionFn } from '../types';

// Bulk +/- runs through the reducer (not an imperative ReactFlow updateNode) so
// the change lands in state.nodes — the controlled source the Tile renders
// from. A single-entity tile's interior size is driven by data.width (the
// auto-width clamp) plus fontSizeVariant, so scaling those in state.nodes is
// what makes the interior resize; an updateNode-only write moves the node box
// but leaves the controlled data stale, so the interior wouldn't update until a
// drag.
export const bulkTileSizeChangeAction: EntityDiagramActionFn<
  BulkTileSizeChangeAction
> = (state, action, _queueEffect) => {
  const widthScale = action.increment
    ? TILE_WIDTH_ADJUSTMENT_INCREMENT
    : TILE_WIDTH_ADJUSTMENT_DECREMENT;
  const variantById = new Map(
    action.nodeChanges.map((change) => [
      change.nodeId,
      change.newFontSizeVariant,
    ])
  );

  const nodes = state.nodes.map((node) => {
    const newFontSizeVariant = variantById.get(node.id);
    if (!newFontSizeVariant || node.type !== 'tile') return node;

    const dataWidth = node.data.width;

    // Auto-width tiles (the default): scale the clamp and let ReactFlow
    // re-measure the box from the new content. Pinning node.width here would
    // round below the sub-pixel natural fit and wrap text (see
    // useSelectedIconShape), so we deliberately leave the node dimensions alone.
    if (
      typeof dataWidth === 'object' &&
      dataWidth !== null &&
      dataWidth.width === 'auto'
    ) {
      return {
        ...node,
        data: {
          ...node.data,
          width: {
            width: 'auto' as const,
            minWidth: dataWidth.minWidth * widthScale,
            maxWidth: dataWidth.maxWidth * widthScale,
          },
          fontSizeVariant: newFontSizeVariant,
          dimensionsFinalized: true,
        },
      };
    }

    // Drag-resized tiles carry an explicit px width: scale it with the font
    // step and keep node.width in sync so layout and the save path read the new
    // size. Clear the explicit height (rather than scaling it) so the tile
    // re-measures to the new font's natural height — scaling a fixed height
    // would clip the reflowed text, and pinning measured.height risks squashing
    // the node. We intentionally do not touch `measured` here; ReactFlow
    // re-measures from the rendered tile.
    if (typeof dataWidth === 'number') {
      const nextWidth = dataWidth * widthScale;
      return {
        ...node,
        width: nextWidth,
        height: undefined,
        data: {
          ...node.data,
          width: nextWidth,
          height: undefined,
          fontSizeVariant: newFontSizeVariant,
          dimensionsFinalized: true,
        },
      };
    }

    // No data.width hint (unexpected for entity tiles): just bump the font.
    return {
      ...node,
      data: {
        ...node.data,
        fontSizeVariant: newFontSizeVariant,
        dimensionsFinalized: true,
      },
    };
  });

  return { ...state, nodes };
};
